<template>
  <div class="h-listl">
    <div class="h-listl__content">
      <div class="h-listl__head">
        <div>
          <h1 class="h-listl__title">Payment requests</h1>
          <p class="h-listl__sub">{{ subtitle }}</p>
        </div>
        <button type="button" class="h-btn h-btn--primary" @click="openRaise">
          <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M12 5v14M5 12h14"/></svg>Raise a request
        </button>
      </div>

      <HermesEmptyState v-if="error" variant="error" title="Couldn't load payment requests" :message="error" @retry="load" />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No payment requests yet"
        message="Ask a customer to pay a one-off amount: Hermes gives you a link to send them, and records the payment when it arrives."
      />
      <div v-else class="h-listl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-payment-requests"
          :initial-sort="{ column: 'created', dir: 'desc' }"
          @row-click="openDetail"
        />
      </div>
    </div>

    <!-- ===== Raise ===== -->
    <HermesDrawer v-model="raise.open" title="Raise a payment request">
      <div class="h-form">
        <label class="h-form__label" for="pr-email">Customer email</label>
        <input id="pr-email" v-model.trim="raise.form.customer_email" class="h-form__input" type="email" autocomplete="off" />
        <label class="h-form__label" for="pr-name">Customer name <span class="h-form__opt">optional</span></label>
        <input id="pr-name" v-model.trim="raise.form.customer_name" class="h-form__input" type="text" maxlength="255" />

        <label class="h-form__label" for="pr-desc">What it is for</label>
        <input id="pr-desc" v-model.trim="raise.form.description" class="h-form__input" type="text" maxlength="200" placeholder="Go-live review: example.com" />

        <div class="h-form__row">
          <div>
            <label class="h-form__label" for="pr-amount">Amount (£)</label>
            <input id="pr-amount" v-model.trim="raise.form.amount" class="h-form__input" type="text" inputmode="decimal" placeholder="250.00" />
          </div>
          <div>
            <label class="h-form__label" for="pr-basis">The amount is</label>
            <select id="pr-basis" v-model="raise.form.amount_basis" class="h-form__input">
              <option value="exclusive">Net — VAT is added</option>
              <option value="inclusive">Gross — includes VAT</option>
            </select>
          </div>
        </div>
        <div class="h-form__row">
          <div>
            <label class="h-form__label" for="pr-rate">VAT rate (%) <span class="h-form__opt">{{ raise.form.amount_basis === 'inclusive' ? 'required' : 'optional' }}</span></label>
            <input id="pr-rate" v-model.trim="raise.form.vat_rate" class="h-form__input" type="text" inputmode="decimal" :placeholder="raise.form.amount_basis === 'inclusive' ? '20' : 'the vendor\'s rate'" />
          </div>
          <div>
            <label class="h-form__label" for="pr-days">Payable for (days)</label>
            <input id="pr-days" v-model.number="raise.form.valid_for_days" class="h-form__input" type="number" min="1" max="30" />
          </div>
        </div>
        <label class="h-form__label" for="pr-site">Site <span class="h-form__opt">optional</span></label>
        <input id="pr-site" v-model.trim="raise.form.site_reference" class="h-form__input" type="text" maxlength="255" placeholder="example.com" />

        <p v-if="raise.error" class="h-form__err">{{ raise.error }}</p>
      </div>
      <template #footer>
        <button type="button" class="h-btn h-btn--secondary" @click="raise.open = false">Cancel</button>
        <button type="button" class="h-btn h-btn--primary" :disabled="raise.busy" @click="submitRaise">
          {{ raise.busy ? 'Raising…' : 'Raise request' }}
        </button>
      </template>
    </HermesDrawer>

    <!-- ===== Detail ===== -->
    <HermesDrawer v-model="detail.open" :title="detail.row ? detail.row.reference : ''">
      <template v-if="detail.row">
        <dl class="h-facts">
          <div><dt>Status</dt><dd v-html="badge(detail.row.status)"></dd></div>
          <div><dt>For</dt><dd>{{ detail.row.description }}</dd></div>
          <div><dt>Customer</dt><dd>{{ detail.row.customer.email || '—' }}<span v-if="detail.row.customer.name"> · {{ detail.row.customer.name }}</span></dd></div>
          <div><dt>Charged</dt><dd>{{ money(detail.row.amount_cents) }}</dd></div>
          <div><dt>Net · VAT</dt><dd>{{ money(detail.row.net_cents) }} + {{ money(detail.row.vat_cents) }} ({{ detail.row.vat_rate }}%, {{ detail.row.amount_basis }})</dd></div>
          <div><dt>Payable until</dt><dd>{{ date(detail.row.valid_until) }}</dd></div>
          <div v-if="detail.row.paid_at"><dt>Paid</dt><dd>{{ date(detail.row.paid_at) }}<span v-if="detail.row.transaction_id"> · transaction #{{ detail.row.transaction_id }}</span></dd></div>
          <div v-if="detail.row.last_attempt && detail.row.last_attempt.status === 'failed'"><dt>Last attempt</dt><dd>failed: {{ detail.row.last_attempt.reason }}<span v-if="detail.row.last_attempt.gateway_reason" class="h-muted"> ({{ detail.row.last_attempt.gateway_reason }})</span></dd></div>
          <div v-if="detail.row.site_reference"><dt>Site</dt><dd>{{ detail.row.site_reference }}</dd></div>
          <div v-if="detail.row.external_reference"><dt>Their reference</dt><dd class="h-mono">{{ detail.row.external_reference }}</dd></div>
        </dl>

        <div class="h-link">
          <input class="h-form__input h-mono" :value="detail.row.pay_url" readonly @focus="$event.target.select()" />
          <button type="button" class="h-btn h-btn--secondary" @click="copy(detail.row.pay_url)">{{ copied ? 'Copied' : 'Copy link' }}</button>
        </div>
        <p class="h-muted h-small">Send this link to the customer. Hermes sends no email.</p>

        <div v-if="canExtend(detail.row)" class="h-form__row h-gap">
          <div>
            <label class="h-form__label" for="pr-ext">Give more time (days)</label>
            <input id="pr-ext" v-model.number="detail.extendDays" class="h-form__input" type="number" min="1" max="30" />
          </div>
          <div class="h-end">
            <button type="button" class="h-btn h-btn--secondary" :disabled="detail.busy" @click="extend">Extend</button>
          </div>
        </div>
        <p v-if="detail.error" class="h-form__err">{{ detail.error }}</p>
      </template>
      <template #footer>
        <button v-if="detail.row && canCancel(detail.row)" type="button" class="h-btn h-btn--danger" :disabled="detail.busy" @click="cancelRequest">
          {{ detail.confirmCancel ? 'Confirm: withdraw this request' : 'Cancel request' }}
        </button>
        <button type="button" class="h-btn h-btn--secondary" @click="detail.open = false">Close</button>
      </template>
    </HermesDrawer>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesPaymentRequests — staff raise and follow payment requests
// (docs/payment-requests.md). Reads and writes /admin/payment-requests; the pay
// link it shows is the same stable /pay/request/{reference} a consuming product
// gets from the API.
const BADGE = {
  requested: ['var(--h-info-bg,#E0F2FE)', 'var(--h-info-text,#0369A1)', 'requested'],
  failed: ['var(--h-warning-bg,#FFFBEB)', 'var(--h-warning-text,#B45309)', 'failed, payable'],
  paid: ['var(--h-success-bg,#ECFDF5)', 'var(--h-success-text,#047857)', 'paid'],
  expired: ['var(--h-bg-subtle,#F1F5F9)', 'var(--h-text-secondary,#64748B)', 'expired'],
  cancelled: ['var(--h-bg-subtle,#F1F5F9)', 'var(--h-text-secondary,#64748B)', 'cancelled'],
};

export default {
  name: 'HermesPaymentRequests',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      loading: true,
      error: null,
      rows: [],
      copied: false,
      raise: { open: false, busy: false, error: '', form: {} },
      detail: { open: false, busy: false, error: '', row: null, extendDays: 5, confirmCancel: false },
      columns: [
        { key: 'reference', label: 'Reference', width: 150, format: (v) => `<span style="font-family:var(--h-font-mono);font-size:12.5px">${this.esc(v)}</span>` },
        { key: 'customer_email', label: 'Customer', format: (v) => this.esc(v) || '—' },
        { key: 'description', label: 'For', format: (v) => this.esc(v) },
        { key: 'amount_cents', label: 'Charged', hozAlign: 'right', width: 110, sorter: 'number', format: (v) => `<span style="font-family:var(--h-font-mono)">${this.money(v)}</span>` },
        { key: 'status', label: 'Status', width: 140, format: (v) => this.badge(v) },
        { key: 'valid_until', label: 'Payable until', width: 130, sorter: 'number', format: (v) => this.date(v) },
        { key: 'created', label: 'Raised', width: 120, sorter: 'number', format: (v) => this.date(v) },
      ],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const open = this.rows.filter((r) => r.status === 'requested' || r.status === 'failed').length;
      const paid = this.rows.filter((r) => r.status === 'paid').length;
      return `${this.rows.length} request${this.rows.length === 1 ? '' : 's'} · ${open} open · ${paid} paid`;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const all = [];
        let cursor = '';
        for (let page = 0; page < 20; page += 1) {
          const data = await this.api.get(`${this.apiBase}/admin/payment-requests${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
          all.push(...(data.items || []));
          if (!data.next_cursor) break;
          cursor = data.next_cursor;
        }
        this.rows = all.map((r) => ({ ...r, customer_email: r.customer ? r.customer.email : '' }));
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load${status ? ` (${status})` : ''}. ${e?.response?.data?.message || e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    openRaise() {
      this.raise = {
        open: true, busy: false, error: '',
        form: { customer_email: '', customer_name: '', description: '', amount: '', amount_basis: 'exclusive', vat_rate: '', valid_for_days: 5, site_reference: '' },
      };
    },
    async submitRaise() {
      const f = this.raise.form;
      const pounds = Number(f.amount);
      if (!f.amount || Number.isNaN(pounds) || pounds <= 0) {
        this.raise.error = 'Enter an amount greater than zero.';
        return;
      }
      const payload = {
        customer_email: f.customer_email,
        description: f.description,
        amount_cents: Math.round(pounds * 100),
        amount_basis: f.amount_basis,
        currency: 'GBP',
        valid_for_days: Number(f.valid_for_days) || 5,
      };
      if (f.customer_name) payload.customer_name = f.customer_name;
      if (f.vat_rate !== '') payload.vat_rate = f.vat_rate;
      if (f.site_reference) payload.site_reference = f.site_reference;

      this.raise.busy = true;
      this.raise.error = '';
      try {
        const data = await this.api.post(`${this.apiBase}/admin/payment-requests`, payload);
        this.raise.open = false;
        await this.load();
        const made = data.payment_request;
        if (made) this.openDetail({ ...made, customer_email: made.customer ? made.customer.email : '' });
      } catch (e) {
        this.raise.error = e?.response?.data?.message || 'Could not raise the request.';
      } finally {
        this.raise.busy = false;
      }
    },
    openDetail(row) {
      this.copied = false;
      this.detail = { open: true, busy: false, error: '', row, extendDays: 5, confirmCancel: false };
    },
    canExtend(row) {
      return ['requested', 'failed', 'expired'].includes(row.status);
    },
    canCancel(row) {
      return ['requested', 'failed', 'expired'].includes(row.status);
    },
    async extend() {
      await this.act(`/extend`, { valid_for_days: Number(this.detail.extendDays) || 0 });
    },
    async cancelRequest() {
      if (!this.detail.confirmCancel) {
        this.detail.confirmCancel = true;
        return;
      }
      await this.act(`/cancel`, {});
    },
    async act(path, body) {
      this.detail.busy = true;
      this.detail.error = '';
      try {
        const data = await this.api.post(`${this.apiBase}/admin/payment-requests/${encodeURIComponent(this.detail.row.reference)}${path}`, body);
        const row = data.payment_request;
        this.detail.row = { ...row, customer_email: row.customer ? row.customer.email : '' };
        this.detail.confirmCancel = false;
        await this.load();
      } catch (e) {
        this.detail.error = e?.response?.data?.message || 'That did not work.';
      } finally {
        this.detail.busy = false;
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copied = true;
      } catch {
        this.copied = false;
      }
    },
    badge(status) {
      const [bg, fg, label] = BADGE[status] || BADGE.expired;
      return `<span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:11.5px;font-weight:600;background:${bg};color:${fg}">${label}</span>`;
    },
    money(pence) {
      if (pence == null) return '—';
      return `£${(Number(pence) / 100).toFixed(2)}`;
    },
    date(v) {
      if (!v) return '—';
      return new Date(Number(v) * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    esc(v) {
      return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-listl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-listl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-listl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-listl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-listl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-listl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }

.h-form { display: flex; flex-direction: column; gap: 6px; }
.h-form__label { font-size: 13px; font-weight: 500; margin-top: 8px; }
.h-form__opt { font-weight: 400; color: var(--h-text-tertiary, #94A3B8); font-size: 12px; }
.h-form__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-form__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.h-form__err { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 10px 0 0; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border-radius: var(--h-radius, 6px); }

.h-facts { margin: 0 0 16px; display: grid; gap: 9px; }
.h-facts > div { display: flex; gap: 12px; font-size: 13px; }
.h-facts dt { width: 120px; flex: none; color: var(--h-text-tertiary, #94A3B8); }
.h-facts dd { margin: 0; }
.h-link { display: flex; gap: 8px; }
.h-mono { font-family: var(--h-font-mono, monospace); font-size: 12.5px; }
.h-muted { color: var(--h-text-tertiary, #94A3B8); }
.h-small { font-size: 12px; margin: 6px 0 0; }
.h-gap { margin-top: 16px; }
.h-end { display: flex; align-items: flex-end; }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
.h-btn__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--danger { background: var(--h-bg-card, #fff); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); margin-right: auto; }

@media (max-width: 640px) {
  .h-form__row { grid-template-columns: 1fr; }
}
</style>
